import {
  DatabaseProvider,
  withDatabase,
} from "@nozbe/watermelondb/DatabaseProvider";
import {
  AuthProvider,
  LanguageProvider,
  SnackbarProvider,
  WalletProvider,
  ThemeProvider,
} from "./context";
import { database } from "./database";
import { useContext, useEffect, useMemo, useState } from "react";
import { createBrowserHistory } from "history";
import { LanguageContext } from "./context/LanguageContext";
import * as Sentry from "@sentry/react";
import Routes from "./Routes";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import Cookies from "js-cookie";
import { analytics, triggerLogEvent } from "./config/firebase";
import { uuid } from "./oscar-pos-core/constants";
import { ThemeContext } from "./context/ThemeContext";

const history = createBrowserHistory();

const createAppMuiTheme = (mode) =>
  createTheme({
    palette: {
      mode,
      primary: {
        main: mode === "dark" ? "#F8FAFC" : "#161B2F",
        contrastText: mode === "dark" ? "#334155" : "#FFFFFF",
      },
      secondary: {
        main: "#94A3B8",
      },
      success: {
        main: mode === "dark" ? "#3CAE8B" : "#4AC600",
      },
      error: {
        main: "#F65454",
      },
      info: {
        main: "#00AEEF",
      },
      background: {
        default: mode === "dark" ? "#1C2028" : "#f8fafc",
        paper: mode === "dark" ? "#21262D" : "#ffffff",
      },
      text: {
        primary: mode === "dark" ? "#F8FAFC" : "#334155",
        secondary: "#94A3B8",
        ...(mode === "dark" ? { disabled: "#334155" } : {}),
      },
      divider: mode === "dark" ? "#2F3640" : "#e2e8f0",
    },
    ...(mode === "dark"
      ? {
          components: {
            MuiCssBaseline: {
              styleOverrides: {
                body: {
                  backgroundColor: "#1C2028",
                  color: "#F8FAFC",
                },
              },
            },
            MuiPaper: {
              styleOverrides: {
                root: {
                  backgroundImage: "none",
                  backgroundColor: "#21262D",
                },
              },
            },
            MuiButton: {
              styleOverrides: {
                containedPrimary: {
                  backgroundColor: "#F8FAFC",
                  color: "#334155",
                  "&:hover": {
                    backgroundColor: "#FFFFFF",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#2F3640",
                    color: "#334155",
                  },
                },
                outlined: {
                  borderColor: "#2F3640",
                  color: "#F8FAFC",
                },
              },
            },
          },
        }
      : {}),
  });

// Exact messages only: "Failed to fetch dynamically imported module …" (chunk load) must still report
const FETCH_FAILURE_MESSAGES = [
  "Failed to fetch",
  "NetworkError when attempting to fetch resource.",
  "Load failed",
];
// Third-party scripts whose failed requests surface as unhandled "Failed to fetch"
const THIRD_PARTY_FETCH_HOSTS = [
  "google-analytics.com",
  "analytics.google.com",
  "googletagmanager.com",
  "clarity.ms",
  "facebook.com",
  "facebook.net",
];

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  integrations: [
    Sentry.reactRouterV4BrowserTracingIntegration({
      history,
      tracePropagationTargets: [process.env.REACT_APP_BASE_URL], //TODO add url udharweb /^https:\/\/yourserver\.io\/api/
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
  beforeSend(event, hint) {
    const error = hint?.originalException;
    const message = error?.message || event?.message || "";
    if (
      message === "Network Error" ||
      (error?.code === "ERR_NETWORK" && !error?.response)
    ) {
      return null;
    }
    // Firebase Analytics init needs an installation id; when the page loads offline the SDK
    // rejects internally (uncaught) — nothing actionable on our side
    if (error?.name === "FirebaseError" && error?.code === "installations/app-offline") {
      return null;
    }
    // Bare fetch failures (Chrome / Firefox / Safari wording) whose last failed request went to a
    // known third-party tracker (GA collect, Clarity, …) — blocked by ad-blockers or a flaky
    // network, never caught by those scripts. Anything else (our own fetch calls) still reports.
    if (error?.name === "TypeError" && FETCH_FAILURE_MESSAGES.includes(message)) {
      const lastFailedFetch = [...(event.breadcrumbs || [])]
        .reverse()
        .find((crumb) => crumb.category === "fetch" && crumb.level === "error");
      const failedUrl = lastFailedFetch?.data?.url || "";
      if (THIRD_PARTY_FETCH_HOSTS.some((host) => failedUrl.includes(host))) {
        return null;
      }
    }
    return event;
  },
});

function AppContent({ isOffline }) {
  const [theme] = useContext(ThemeContext);
  const muiTheme = useMemo(
    () => createAppMuiTheme(theme === "dark" ? "dark" : "light"),
    [theme]
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem("rupinOnboardingAnimationSeen", "true");
    }, 3000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <MuiThemeProvider theme={muiTheme}>
      <Routes isOffline={isOffline} />
    </MuiThemeProvider>
  );
}

function App() {
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    let deviceId = Cookies.get("deviceId");
    if (!deviceId) {
      deviceId = uuid();
      Cookies.set("deviceId", deviceId, { expires: 36500 });
    }
  }, []);

  // Once per page load: was the user logged in, a guest, or logged out when they landed?
  // tempUserId is checked first: guests can also have phone_number saved (SignUp stores it for
  // a new number before CreateBusiness), and tempUserId is removed once they register.
  useEffect(() => {
    const userState = Cookies.get("tempUserId")
      ? "guest"
      : localStorage.getItem("phone_number")
        ? "logged_in"
        : "logged_out";
    triggerLogEvent(analytics, "app_landing", {
      user_state: userState,
      landing_path: window.location.pathname.slice(0, 100), // GA4 param value limit
    });
  }, []);

  useEffect(() => {
    window.addEventListener("offline", (e) => handleOffline(true));
    window.addEventListener("online", () => handleOffline(false));
  }, [navigator.onLine]);

  const handleOffline = (isOff) => setIsOffline(isOff);

  return (
    <DatabaseProvider database={database}>
      <AuthProvider>
        <ThemeProvider>
          <LanguageProvider>
            <SnackbarProvider>
              <WalletProvider>
                <AppContent isOffline={isOffline} />
              </WalletProvider>
            </SnackbarProvider>
          </LanguageProvider>
        </ThemeProvider>
      </AuthProvider>
    </DatabaseProvider>
  );
}

export default withDatabase(
  Sentry.withErrorBoundary(App, {
    fallback: (
      <h2>
        an error has occurred. Please contact Support{" "}
        <h2 style={{ color: "var(--foundationBlueDark)" }}>02138341166</h2>
      </h2>
    ),
  }),
);
